import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    EditButton,
    ShowButton,
    DeleteButton,
    Filter,
    TextInput,
    SearchInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Box,
    Typography,
    Chip,
    Fab,
    useMediaQuery,
    useTheme,
    Tooltip,
} from '@mui/material';
import {
    Groups,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const UserGroupFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.user-groups.list.filters.search')}
                source="search"
                alwaysOn
            />
        </Filter>
    );
};

const MobileUserGroupFilter = (props: any) => (
    <Filter {...props}>
        <SearchInput source="search" alwaysOn />
    </Filter>
);

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0, gap: 1 }}>
        <ExportButton />
        <RefreshButton />
    </Box>
);

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/user-groups/${record.id}/show`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Groups fontSize="small" color="primary" />
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {record.name}
                            </Typography>
                            <Chip
                                size="small"
                                label={`${record.memberCount ?? 0}`}
                                color="default"
                                sx={{ flexShrink: 0 }}
                            />
                        </Box>
                        {record.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                                {record.description}
                            </Typography>
                        )}
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const UserGroupList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={isMobile ? <MobileUserGroupFilter /> : <UserGroupFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                sort={{ field: 'name', order: 'ASC' }}
                perPage={25}
                empty={isMobile ? false : undefined}
                title={translate('resources.user-groups.list.title')}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick="show"
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                        }}
                    >
                        <TextField source="name" label={translate('resources.user-groups.fields.name')} />
                        <TextField source="description" label={translate('resources.user-groups.fields.description')} />
                        <FunctionField
                            label={translate('resources.user-groups.fields.memberCount')}
                            render={(record: any) => (
                                <Chip
                                    label={record.memberCount ?? 0}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        />
                        <DateField source="createdAt" label={translate('resources.user-groups.fields.createdAt')} showTime />
                        <FunctionField
                            label=""
                            render={(record: any) => (
                                <Box display="flex" gap={0}>
                                    <Tooltip title={translate('ra.action.show')}>
                                        <span><ShowButton record={record} size="small" label="" /></span>
                                    </Tooltip>
                                    <Tooltip title={translate('ra.action.edit')}>
                                        <span><EditButton record={record} size="small" label="" /></span>
                                    </Tooltip>
                                    <Tooltip title={translate('ra.action.delete')}>
                                        <span><DeleteButton record={record} size="small" label="" /></span>
                                    </Tooltip>
                                </Box>
                            )}
                            source="actions"
                            textAlign="right"
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/user-groups/create')}
                    sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};

export default UserGroupList;
